// src/components/LoadingScreen.tsx
import * as React from 'react';
const { useState, useEffect } = React;
import { useLoading } from '@/contexts/LoadingContext';
import { useAppInitialization } from '@/hooks/useAppInitialization';
import { useSmartLoading } from '@/hooks/useSmartLoading';
import { APP_IMAGES } from '@/utils/storage';
import BibleBookIcon from '@/components/icons/BibleBookIcon';

interface LoadingScreenProps {
  message?: string;
}

const BIBLE_VERSES = [
  "Be still, and know that I am God. - Psalm 46:10",
  "The Lord your God is with you, the Mighty Warrior who saves. - Zephaniah 3:17",
  "For I know the plans I have for you, declares the Lord. - Jeremiah 29:11",
  "Cast all your anxiety on him because he cares for you. - 1 Peter 5:7",
  "In all your ways submit to him, and he will make your paths straight. - Proverbs 3:6"
];

const LOADING_PHASES = [
  { message: "Starting Abiel...", duration: 1000 },
  { message: "Loading your data...", duration: 1500 },
  { message: "Preparing content...", duration: 1000 },
];

const LoadingScreen: React.FC<LoadingScreenProps> = ({ message }) => {
  const { loadingStates, isAnyLoading, getLoadingProgress } = useLoading();
  const { progress: appProgress, currentPhase: initPhase, isComplete } = useAppInitialization();
  const { currentVerse, currentTip, personalizedMessage, getContextualMessage } = useSmartLoading();
  const [currentPhase, setCurrentPhase] = useState(0);
  const [progress, setProgress] = useState(0);
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  const [showVerse, setShowVerse] = useState(false);
  const [showTip, setShowTip] = useState(false);
  
  // Enhanced content timing
  useEffect(() => {
    // Show verse after initial delay
    const showVerseTimer = setTimeout(() => {
      setShowVerse(true);
    }, 1500);
    
    // Show tip after verse
    const showTipTimer = setTimeout(() => {
      setShowTip(true);
    }, 4000);

    return () => {
      clearTimeout(showVerseTimer);
      clearTimeout(showTipTimer);
    };
  }, []);

  // Enhanced progress tracking
  useEffect(() => {
    if (!message) {
      // Use app initialization progress if available
      if (appProgress > 0) {
        setProgress(appProgress);
      } else {
        // Fallback to original phase-based progress
        let phaseIndex = 0;
        let progressValue = 0;
        
        const runPhase = () => {
          if (phaseIndex < LOADING_PHASES.length) {
            setCurrentPhase(phaseIndex);
            const phase = LOADING_PHASES[phaseIndex];
            const increment = 100 / LOADING_PHASES.length;
            
            const progressTimer = setInterval(() => {
              progressValue += increment / (phase.duration / 50);
              setProgress(Math.min(progressValue, (phaseIndex + 1) * increment));
            }, 50);
            
            setTimeout(() => {
              clearInterval(progressTimer);
              phaseIndex++;
              runPhase();
            }, phase.duration);
          }
        };
        
        runPhase();
      }
    }
  }, [message, appProgress]);
  
  // Get enhanced display message
  const getDisplayMessage = () => {
    if (message) return message;
    
    // Check for specific loading states with priorities
    const loadingValues = Object.values(loadingStates);
    if (loadingValues.length > 0) {
      // Priority: initialization > audio > action > page > general
      const priorityOrder = ['initialization', 'audio', 'action', 'page', 'general'];
      const prioritizedState = priorityOrder
        .map(type => loadingValues.find(state => state.type === type))
        .find(state => state);
      
      if (prioritizedState) {
        return prioritizedState.message;
      }
      
      return loadingValues[0]?.message || 'Loading...';
    }
    
    // Use contextual message based on progress
    if (appProgress > 0) {
      return getContextualMessage(appProgress);
    }
    
    return LOADING_PHASES[currentPhase]?.message || 'Loading...';
  };

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center min-h-screen bg-background text-foreground animate-fade-in z-50">
      {/* App Icon */}
      <div className="mb-6 animate-scale-in">
        <img
          src="/lovable-uploads/6a554785-f0b6-4394-962c-17ceda97e056.png"
          alt="Loading"
          className="w-32 h-32 mb-6 animate-pulse rounded-lg shadow-lg"
        />
      </div>
      
      {/* App Title */}
      <h1 className="text-3xl font-bold mb-2 text-foreground animate-fade-in">
        Abiel
      </h1>
      
      {/* Enhanced Tagline with personalization */}
      <p className="text-sm text-muted-foreground mb-2 text-center px-4 max-w-sm animate-fade-in">
        The Bible App That Reads, Reflects, and Prays With You.
      </p>
      
      {/* Personalized welcome message */}
      {personalizedMessage && (
        <p className="text-xs text-muted-foreground mb-6 text-center px-4 max-w-sm animate-fade-in opacity-80">
          {personalizedMessage}
        </p>
      )}
      
      {/* Enhanced Progress Bar */}
      <div className="w-64 h-2 bg-muted rounded-full mb-2 overflow-hidden animate-fade-in">
        <div 
          className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      
      {/* Progress percentage */}
      <p className="text-xs text-muted-foreground mb-4 animate-fade-in">
        {Math.round(progress)}%
      </p>
      
      {/* Dynamic Loading Message */}
      <p className="text-sm text-muted-foreground mb-6 text-center px-4 animate-fade-in min-h-[20px]">
        {getDisplayMessage()}
      </p>
      
      {/* Loading Spinner */}
      <div className="relative mb-8">
        <div className="w-8 h-8 border-2 border-muted border-t-primary rounded-full animate-spin" />
      </div>
      
      {/* Enhanced Content Section */}
      <div className="max-w-lg px-6 space-y-4">
        {/* Smart Bible Verse */}
        {showVerse && (
          <div className="animate-fade-in">
            <p className="text-xs text-muted-foreground text-center italic leading-relaxed">
              {currentVerse}
            </p>
          </div>
        )}
        
        {/* Loading Tip */}
        {showTip && (
          <div className="animate-fade-in">
            <p className="text-xs text-muted-foreground/80 text-center leading-relaxed">
              {currentTip}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingScreen;
